/**
 * A language tag guessed from the script a track's text is written in, for a writer that has
 * to declare one when the source never did.
 *
 * SAMI declares a language per class (`lang: ko-KR`), and an SRT carries none, so every SMI made
 * from an SRT said `en-US`: a player that shows the track language in its menu called Korean
 * subtitles "English" (SEO_BRIEF_SMI section 5; the founder chose automatic detection,
 * 2026-10-08). Only scripts that name one language are guessed. Hangul is Korean and kana is
 * Japanese; Han with no kana beside it is Chinese. Latin text could be any of dozens of
 * languages, so it gets no guess and the writer keeps its old default.
 */

/** Enough text to decide; the share of each script does not move after this. */
const SAMPLE_CHARS = 20_000;

const isHangul = (cp: number): boolean =>
  (cp >= 0xac00 && cp <= 0xd7a3) ||
  (cp >= 0x1100 && cp <= 0x11ff) ||
  (cp >= 0x3130 && cp <= 0x318f);
const isKana = (cp: number): boolean => cp >= 0x3040 && cp <= 0x30ff;
const isHan = (cp: number): boolean =>
  (cp >= 0x4e00 && cp <= 0x9fff) || (cp >= 0x3400 && cp <= 0x4dbf);
const isLetter = (ch: string): boolean => /\p{L}/u.test(ch);

/**
 * `ko-KR`, `ja-JP`, `zh-CN`, or null when the text does not settle it.
 *
 * Shares are of LETTERS, so punctuation, digits and spaces don't dilute them. Korean and
 * Japanese subtitles routinely carry English words and names, so a fifth of the letters is
 * enough to call the track: real Korean dialogue sits far above that, and English dialogue that
 * quotes one Korean word sits far below.
 */
export function languageFromScript(texts: Iterable<string>): string | null {
  let letters = 0;
  let hangul = 0;
  let kana = 0;
  let han = 0;
  let seen = 0;
  outer: for (const text of texts) {
    for (const ch of text) {
      if (++seen > SAMPLE_CHARS) break outer;
      if (!isLetter(ch)) continue;
      letters++;
      const cp = ch.codePointAt(0) as number;
      if (isHangul(cp)) hangul++;
      else if (isKana(cp)) kana++;
      else if (isHan(cp)) han++;
    }
  }
  if (letters === 0) return null;
  if (hangul / letters >= 0.2) return 'ko-KR';
  // Japanese is Han and kana together; kana is the half no other language writes.
  if (kana / letters >= 0.05) return 'ja-JP';
  if (han / letters >= 0.2) return 'zh-CN';
  return null;
}
